import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://tantra-map.com';

/**
 * SEO Component for dynamic meta tags and Schema.org structured data
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description (max 160 chars recommended)
 * @param {string} props.image - OG image URL
 * @param {string} props.url - Canonical URL path (e.g., "/teacher/123")
 * @param {string} props.type - OG type (website, article, profile)
 * @param {Object} props.schema - Schema.org JSON-LD object
 * @param {string} props.author - Article author name
 * @param {string} props.publishedTime - ISO date string for articles
 */
export function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  schema,
  author,
  publishedTime,
  noindex = false
}) {
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const fullTitle = title ? `${title} | Tantra Map` : 'Tantra Map – Encuentra experiencias tántricas auténticas';
  const metaDescription = description || 'Explora Tantra Map y descubre espacios, terapeutas y experiencias tántricas auténticas en todo el mundo.';
  const ogImage = image || `${BASE_URL}/preview.jpg`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Tantra Map" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article specific */}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}

      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Generate Schema.org Person for teachers/professionals
 */
export function generatePersonSchema(teacher) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: teacher.user_name || teacher.name,
    description: typeof teacher.bio === 'object' ? (teacher.bio.es || teacher.bio.en || '') : teacher.bio,
    image: teacher.images?.[0],
    jobTitle: 'Terapeuta Tántrico',
    knowsAbout: teacher.specialties || [],
    address: teacher.location ? {
      '@type': 'PostalAddress',
      addressLocality: teacher.location.city,
      addressRegion: teacher.location.province,
      addressCountry: teacher.location.country
    } : undefined,
    url: `https://tantra-map.com/teacher/${teacher.id}`,
    sameAs: teacher.social_links ? Object.values(teacher.social_links).filter(Boolean) : [],
    ...(teacher.google_rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: teacher.google_rating,
        reviewCount: teacher.google_reviews_count || 1,
        bestRating: 5
      }
    })
  };
}

/**
 * Generate Schema.org LocalBusiness/Organization for schools
 */
export function generateOrganizationSchema(school) {
  const name = typeof school.name === 'object' ? (school.name.es || school.name.en || '') : school.name;
  const description = typeof school.description === 'object' ? (school.description.es || school.description.en || '') : school.description;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `https://tantra-map.com/school/${school.id}`,
    name: name,
    description: description,
    image: school.images?.[0],
    telephone: school.phone,
    url: school.website || `https://tantra-map.com/school/${school.id}`,
    address: school.location ? {
      '@type': 'PostalAddress',
      streetAddress: school.location.address,
      addressLocality: school.location.city,
      addressRegion: school.location.province,
      postalCode: school.location.postal_code,
      addressCountry: school.location.country
    } : undefined,
    geo: school.location?.latitude ? {
      '@type': 'GeoCoordinates',
      latitude: school.location.latitude,
      longitude: school.location.longitude
    } : undefined,
    sameAs: school.social_links ? Object.values(school.social_links).filter(Boolean) : [],
    ...(school.google_rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: school.google_rating,
        reviewCount: school.google_reviews_count || 1,
        bestRating: 5
      }
    })
  };
}

/**
 * Generate Schema.org Service for services
 */
export function generateServiceSchema(service, provider) {
  const title = typeof service.title === 'object' ? (service.title.es || service.title.en || '') : service.title;
  const description = typeof service.description === 'object' ? (service.description.es || service.description.en || '') : service.description;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: title,
    description: description,
    image: service.images?.[0],
    provider: provider ? {
      '@type': 'Person',
      name: provider.user_name || provider.name
    } : undefined,
    areaServed: service.location ? {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: service.location.city,
        addressCountry: service.location.country
      }
    } : undefined,
    offers: service.price ? {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: service.currency || 'EUR',
      availability: 'https://schema.org/InStock'
    } : undefined,
    url: `https://tantra-map.com/service/${service.id}`
  };
}

/**
 * Generate Schema.org Event for courses
 */
export function generateEventSchema(course, organizer) {
  const title = typeof course.title === 'object' ? (course.title.es || course.title.en || '') : course.title;
  const description = typeof course.description === 'object' ? (course.description.es || course.description.en || '') : course.description;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: title,
    description: description,
    image: course.images?.[0],
    url: `https://tantra-map.com/course/${course.id}`,
    organizer: organizer ? {
      '@type': course.school_id ? 'Organization' : 'Person',
      name: organizer.name || organizer.user_name
    } : undefined,
    eventAttendanceMode: course.is_online 
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled'
  };

  // Add dates if available
  if (course.start_date) {
    schema.startDate = course.start_date;
  }
  if (course.end_date) {
    schema.endDate = course.end_date;
  }

  // Add location
  if (!course.is_online && course.location) {
    schema.location = {
      '@type': 'Place',
      name: course.location.city,
      address: {
        '@type': 'PostalAddress',
        addressLocality: course.location.city,
        addressRegion: course.location.province,
        addressCountry: course.location.country
      }
    };
  } else if (course.is_online) {
    schema.location = {
      '@type': 'VirtualLocation',
      url: `https://tantra-map.com/course/${course.id}`
    };
  }

  // Add offers/pricing
  if (course.price) {
    schema.offers = {
      '@type': 'Offer',
      price: course.price,
      priceCurrency: course.currency || 'EUR',
      availability: 'https://schema.org/InStock',
      validFrom: new Date().toISOString()
    };
  }

  return schema;
}

/**
 * Generate Schema.org Article for blog posts
 */
export function generateArticleSchema(article) {
  const title = typeof article.title === 'object' ? (article.title.es || article.title.en || '') : article.title;
  const description = typeof article.excerpt === 'object' ? (article.excerpt.es || article.excerpt.en || '') : article.excerpt;
  const slug = typeof article.slug === 'object' ? (article.slug.es || article.slug.en || '') : article.slug;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: article.featured_image,
    author: {
      '@type': 'Person',
      name: article.author_name || 'Tantra Map'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Tantra Map',
      logo: {
        '@type': 'ImageObject',
        url: 'https://tantra-map.com/logo.png'
      }
    },
    datePublished: article.published_at,
    dateModified: article.updated_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://tantra-map.com/blog/${slug}`
    }
  };
}

export default SEO;
